// src/components/Fields/DateFieldComponent.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormControl, useTheme } from '@mui/material';
// We no longer use CustomFieldWrapper as the textField slot;
// MUI X will render its own TextField and we will style it
// to match CustomFieldWrapper + TextFieldComponent.
// import CustomFieldWrapper from './CustomFieldWrapper';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { parse, isValid as isValidDate, format as formatDateFns } from 'date-fns';
import { useFormTheme } from '../../contexts/FormThemeContext';

/**
 * Convert metadata-style format (e.g. "MM/DD/YYYY") plus separator
 * into a date-fns / MUI-compatible format (e.g. "MM/dd/yyyy").
 *
 * Supported tokens in metadata:
 * - MM, DD, D, YYYY, YY
 * - MMM, MMMM (month text)
 */
function buildMuiDateFormat(metadataFormat = 'MM/DD/YYYY', separator) {
  let fmt = metadataFormat;

  // If a separator is provided ('-', '/', ' ', '_', ''), normalize all common separators
  if (typeof separator === 'string') {
    fmt = fmt.replace(/[\/\-_ ]/g, separator);
  }

  // Map metadata tokens → date-fns tokens
  fmt = fmt.replace(/YYYY/g, 'yyyy');
  fmt = fmt.replace(/YY/g, 'yy');
  fmt = fmt.replace(/DD/g, 'dd');  // two-digit day of month
  fmt = fmt.replace(/D/g, 'd');    // single-digit day of month (e.g. "MMMM D")
  // Note: we deliberately do NOT touch MMM / MMMM; date-fns understands them

  return fmt;
}


/**
 * Capitalization helpers for formatted date strings.
 */
function applyCapitalization(str, mode) {
  if (!str) return str;

  switch (mode) {
    case 'all-caps':
      return str.toUpperCase();
    case 'no-caps':
      return str.toLowerCase();
    case 'first-letter':
      return str.charAt(0).toUpperCase() + str.slice(1);
    default:
      return str;
  }
}

/**
 * Normalize metadata spacing tokens (e.g. "hyphen", "no-spacing")
 * into actual separator characters for date-fns.
 */
function normalizeDateSeparator(spacing) {
  if (spacing == null) return undefined;

  switch (spacing) {
    case 'hyphen':
      return '-';
    case 'underscore':
      return '_';
    case 'no-spacing':
      return '';
    case 'none':
      // "No restrictions" → fall back to the classic slash default
      return '/';
    default:
      // Already a real separator like "-", "/", " ", "_", or ""
      return spacing;
  }
}

/**
 * Parse a string into a Date using date-fns and the given pattern.
 */
function parseStringToDate(value, pattern) {
  if (!value || !pattern) return null;
  const parsed = parse(value, pattern, new Date());
  return isValidDate(parsed) ? parsed : null;
}

/**
 * Format a Date into a string using date-fns and the given pattern,
 * then apply capitalization if requested.
 */
function formatDateToString(date, pattern, capitalizeMode) {
  if (!date || !pattern) return '';
  const base = formatDateFns(date, pattern);
  return applyCapitalization(base, capitalizeMode);
}

/**
 * Styling helper copied from CustomFieldWrapper so date fields
 * use the same visual language (borders, fonts, colors).
 */
function getVariantStyles(
  variant,
  theme,
  customStyles,
  focusColor,
  disabled,
  error,
  colors,
  fonts
) {
  let baseStyles = {
    '& .Mui-disabled': {
      color: theme.palette.text.disabled,
      '& .MuiOutlinedInput-notchedOutline, & .MuiFilledInput-underline:before, & .MuiInput-underline:before':
        {
          borderColor: theme.palette.action.disabledBackground,
        },
      '& .MuiInputLabel-root': {
        color: theme.palette.text.disabled,
      },
    },
    ...(error && {
      color: theme.palette.error.main,
      '& .MuiOutlinedInput-notchedOutline, & .MuiFilledInput-underline:before, & .MuiInput-underline:before, & .MuiInput-underline:after':
        {
          borderColor: theme.palette.error.main,
        },
      '& .MuiInputLabel-root': {
        color: theme.palette.error.main,
      },
    }),
    // Apply theme-dependent styles for all variants
    color: colors.textColor,
    fontFamily: fonts.secondaryFont, // Use secondaryFont for input text by default
    '& .MuiInputLabel-root': {
      color: colors.textColor,
      fontFamily: fonts.primaryFont, // Use primaryFont for labels
      '&.Mui-focused': {
        color: focusColor || colors.activeColor, // Apply active color when focused
      },
    },
    '& .MuiOutlinedInput-root': {
      borderColor: colors.activeColor,
      '&:hover fieldset': {
        borderColor: colors.activeColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: focusColor || colors.activeColor,
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: colors.backgroundColor,
      '&:hover': {
        backgroundColor: colors.backgroundColor,
      },
      '&.Mui-focused': {
        backgroundColor: colors.backgroundColor,
        borderColor: focusColor || colors.activeColor,
        boxShadow: `0px 0px 0px 2px ${focusColor || colors.activeColor}`, // Optional: add focus shadow
      },
    },
    '& .MuiInput-root': {
      borderBottom: `2px solid ${colors.activeColor}`,
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: `2px solid ${colors.activeColor}`,
      },
      '&.Mui-focused:before': {
        borderBottom: `2px solid ${focusColor || colors.activeColor}`,
      },
      '&.MuiInput-underline:after': {
        borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color after input
      },
    },
    // Apply custom styles if provided
    ...customStyles,
  };

  // Apply variant-specific styles
  switch (variant) {
    case 'outlined':
      return {
        ...baseStyles,
        '& .MuiOutlinedInput-root': {
          borderColor: focusColor || colors.activeColor,
          '&:hover fieldset': {
            borderColor: colors.activeColor,
          },
          '&.Mui-focused fieldset': {
            borderColor: focusColor || colors.activeColor,
          },
        },
      };
    case 'filled':
      return {
        ...baseStyles,
        '& .MuiFilledInput-root': {
          backgroundColor: theme.palette.grey[100],
          '&:hover': {
            backgroundColor: theme.palette.grey[200],
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.grey[100],
            borderColor: focusColor || colors.activeColor,
            boxShadow: `0px 0px 0px 2px ${focusColor || colors.activeColor}`, // Optional: add focus shadow
          },
          '&:before': {
            borderBottom: `2px solid transparent`, // Hide default blue bottom border
          },
          '&:after': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color for the bottom border
          },
        },
      };
    case 'standard':
      return {
        ...baseStyles,
        '& .MuiInput-root': {
          '&:before': {
            borderBottom: `1px solid ${theme.palette.divider}`, // Default unfocused color
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Active color on hover
          },
          '&.Mui-focused:before': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Active color on focus
          },
          '&.MuiInput-underline:after': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color after input
          },
        },
      };
    default:
      return baseStyles;
  }
}

/**
 * MUI X single-date field, wired to your FieldRenderer contract.
 * - `value` is always a STRING (e.g. "11/21/2025" or "Nov 21")
 * - Internally uses Date objects for the picker, but always emits strings.
 */
const DateFieldComponent = ({
  fieldId,
  fieldMetadata = {},
  value: propValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false,
  setShowDropZones,
  variant = 'outlined',
  error,
  helperText,
  externalHelperText,
  label,
  placeholder,
  style,                // ← builder: from HoveredField; final: from FieldRenderer.fieldStyle
  required,
  readOnly,
  transformations = {},
}) => {
  const theme = useTheme();
  const { colors, fonts, fieldVariant } = useFormTheme(); // from FormThemeContext

  // Even if metadata.dateMode says 'range', we treat everything as single-date.
  const metadataFormat = fieldMetadata.dateFormat || 'MM/DD/YYYY';

  // For date fields, DatePickerSettings writes real separator characters
  // like '-', '/', ' ', '_', or '' into transformations.spacing.
  // Legacy text-field tokens (e.g. "hyphen", "no-spacing") are normalized here.
  const rawSpacing = fieldMetadata.transformations?.spacing;
  const separator =
    typeof rawSpacing === 'string' ? normalizeDateSeparator(rawSpacing) : undefined;

  const capitalizeMode =
    transformations.capitalize ??
    fieldMetadata.transformations?.capitalize ??
    'none';


  const encodeOnSubmit =
    typeof transformations.encode === 'boolean'
      ? transformations.encode
      : !!fieldMetadata.transformations?.encode;

  const muiFormat = useMemo(
    () => buildMuiDateFormat(metadataFormat, separator),
    [metadataFormat, separator]
  );

  const [internalValue, setInternalValue] = useState(propValue || '');
  const [dateValue, setDateValue] = useState(null);

  // Sync internal string + Date when parent value changes
  useEffect(() => {
    const next = propValue || '';
    setInternalValue(next);
    setDateValue(parseStringToDate(next, muiFormat));
  }, [propValue, muiFormat]);

  const effectiveHelperText = error ? externalHelperText : helperText;

  const emitChange = useCallback(
    (newStringValue) => {
      setInternalValue(newStringValue);

      if (isBuilderMode && typeof setShowDropZones === 'function') {
        setShowDropZones(false);
      }

      if (typeof onChange === 'function') {
        // 🔑 Match TextFieldComponent: event-like shape
        onChange({ target: { value: newStringValue } });
      }
    },
    [isBuilderMode, setShowDropZones, onChange]
  );

  const handleInputFocus = useCallback(
    (e) => {
      if (isBuilderMode && typeof setShowDropZones === 'function') {
        setShowDropZones(false);
      }
      if (typeof onFocus === 'function') {
        onFocus(e);
      }
    },
    [isBuilderMode, setShowDropZones, onFocus]
  );

  const handleInputBlur = useCallback(
    (e) => {
      if (isBuilderMode && typeof setShowDropZones === 'function') {
        setShowDropZones(true);
      }
      if (typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    [isBuilderMode, setShowDropZones, onBlur]
  );

  const handleDateChange = useCallback(
    (newDate) => {
      setDateValue(newDate);

      if (!newDate) {
        emitChange('');
        return;
      }

      const formatted = formatDateToString(newDate, muiFormat, capitalizeMode);
      emitChange(formatted);
    },
    [emitChange, muiFormat, capitalizeMode]
  );

  // Encode on submit when requested (mirrors TextFieldComponent behaviour)
  useEffect(() => {
    if (!isSubmitting) return;

    let finalValue = internalValue || '';
    if (encodeOnSubmit && finalValue) {
      finalValue = encodeURIComponent(finalValue);
    }

    if (typeof onChange === 'function') {
      onChange({ target: { value: finalValue } });
    }
  }, [isSubmitting, internalValue, encodeOnSubmit, onChange]);

  // Use the themed variant if provided; fall back to prop, then 'outlined'
  const actualVariant = fieldVariant || variant || 'outlined';

  // Prefer explicit placeholder, then metadata placeholder, then hint, then format
  const effectivePlaceholder =
    placeholder ||
    fieldMetadata.placeholder ||
    fieldMetadata.hint ||
    muiFormat.toLowerCase();

  // 🔎 Determine which custom styles to use:
  //  - In BUILDER: ignore `style` (it comes from HoveredField, not layout).
  //  - In FINAL (preview/published): use layout style from FieldRenderer,
  //    BUT strip properties that cause vertical misalignment in the row.
  const layoutCustomStyles = useMemo(() => {
  if (isBuilderMode) {
    // In the builder, TextFieldComponent ignores `style` completely.
    // Do the same here so Date fields follow the same wrapper rules.
    return {};
  }

  if (!style) return {};

  // Destructure to drop backgroundColor AND ALL margins/padding
  // so the inner TextField does not introduce extra spacing
  // compared to TextFieldComponent. Row-level wrappers +
  // gap handle spacing for all fields.
  const {
    backgroundColor,
    paddingTop,
    paddingBottom,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    ...rest
  } = style;

  return rest;
}, [isBuilderMode, style]);


  // Align date field styling with other fields (equivalent to what
  // CustomFieldWrapper does for TextFieldComponent).
  const variantStyles = useMemo(
    () =>
      getVariantStyles(
        actualVariant,
        theme,
        layoutCustomStyles,               // ⬅ use only safe, layout-intended styles
        undefined,                        // focusColor (use colors.activeColor)
        disabled || isSubmitting,
        error,
        colors,
        fonts
      ),
    [actualVariant, theme, layoutCustomStyles, disabled, isSubmitting, error, colors, fonts]
  );

  return (
    <FormControl
      fullWidth
      disabled={disabled || isSubmitting}
      // field-level layout styles only; input styling is handled via variantStyles
      sx={{ width: '100%' }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          label={label || fieldMetadata.label || 'Select date'}
          inputFormat={muiFormat}
          value={dateValue}
          onChange={handleDateChange}
          disabled={disabled || isSubmitting}
          // Let MUI X render its own TextField, but inject the
          // same styling & behavior that CustomFieldWrapper
          // applies to TextFieldComponent.
slotProps={{
  textField: {
    variant: actualVariant,
    required: fieldMetadata.required,
    error,
    helperText: effectiveHelperText,
    placeholder: effectivePlaceholder,
    fullWidth: true,
    disabled: disabled || isSubmitting,
    margin: 'none',
    size: 'medium',
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
    InputProps: {
      style: {
        color: colors.textColor,
        fontFamily: fonts.secondaryFont,
        backgroundColor:
          disabled || isSubmitting ? 'rgba(0, 0, 0, 0.06)' : undefined,
      },
    },
    sx: {
      ...variantStyles,
    },
  },
desktopPaper: {
  sx: {
    minWidth: '100%',
  },
},


}}

        />
      </LocalizationProvider>
    </FormControl>
  );
};

DateFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    dateMode: PropTypes.oneOf(['single', 'range']), // we ignore 'range' and treat as single
    dateFormat: PropTypes.string,
    placeholder: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.shape({
      capitalize: PropTypes.oneOf(['none', 'all-caps', 'no-caps', 'first-letter']),
      spacing: PropTypes.string, // actual separator: '-', '/', ' ', '_', or ''
      encode: PropTypes.bool,
    }),
  }),
  value: PropTypes.string, // canonical stored value (e.g., "11/21/2025" or "Nov 21")
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  externalHelperText: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  transformations: PropTypes.object,
};

export default DateFieldComponent;
